import React, { useEffect, useMemo, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToast,
} from "@ionic/react";

import Header from "src/components/Header";
import {
  buscarPuntosServicioVisitaUE,
  crearVisitaUE,
  listarOpcionesVisitaUE,
  listarVisitasUE,
  obtenerAccesoVisitaUE,
  obtenerContextoVisitaUE,
  AccesoVisitaUE,
  ContextoVisitaUE,
  OpcionesVisitaUEResponse,
  PreguntaUE,
  PuntoServicioUE,
  VisitaUERegistro,
} from "src/services/visitasUnidadesEspecializadas";

const normalizarFecha = (value: string | string[] | null | undefined) => {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  return raw.toString().slice(0, 10);
};

const VisitasUnidadesEspecializadas: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [acceso, setAcceso] = useState<AccesoVisitaUE | null>(null);
  const [opciones, setOpciones] = useState<OpcionesVisitaUEResponse | null>(null);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({
    show: false,
    msg: "",
  });

  const [psBusqueda, setPsBusqueda] = useState("");
  const [psBuscando, setPsBuscando] = useState(false);
  const [psResultados, setPsResultados] = useState<PuntoServicioUE[]>([]);
  const [psSeleccionado, setPsSeleccionado] = useState<PuntoServicioUE | null>(null);
  const [contexto, setContexto] = useState<ContextoVisitaUE | null>(null);

  // "No es cliente": permite tipear el lugar en vez de buscar un PS del sistema.
  const [noEsCliente, setNoEsCliente] = useState(false);
  const [psNombreLibre, setPsNombreLibre] = useState("");

  const [fechaVisita, setFechaVisita] = useState<string | undefined>(undefined);
  const [sector, setSector] = useState("");
  const [responsableVisita, setResponsableVisita] = useState("");
  const [motivoVisita, setMotivoVisita] = useState<string>("");
  const [respuestas, setRespuestas] = useState<Record<string, string>>({});

  const [historial, setHistorial] = useState<VisitaUERegistro[]>([]);
  const [listando, setListando] = useState(false);

  const preguntasOrdenadas = useMemo(
    () =>
      [...(opciones?.preguntas || [])].sort(
        (a, b) => (a.orden ?? 0) - (b.orden ?? 0)
      ),
    [opciones]
  );

  const cargarHistorial = async () => {
    setListando(true);
    try {
      const datos = await listarVisitasUE();
      setHistorial(datos);
    } catch (error: any) {
      setToast({
        show: true,
        msg: error?.message || "No se pudo cargar el historial.",
        color: "warning",
      });
    } finally {
      setListando(false);
    }
  };

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const acc = await obtenerAccesoVisitaUE();
        if (!activo) return;
        setAcceso(acc);
        if (!acc?.tiene_acceso) {
          setCargando(false);
          return;
        }
        const data = await listarOpcionesVisitaUE();
        if (!activo) return;
        setOpciones(data);
        const responsableDefecto = data?.campos_cabecera?.responsable_visita?.valor_defecto;
        if (responsableDefecto) setResponsableVisita(responsableDefecto);
        await cargarHistorial();
      } catch (error: any) {
        if (!activo) return;
        setToast({
          show: true,
          msg: error?.message || "No se pudo cargar Visitas Unidades Especializadas.",
          color: "danger",
        });
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    const termino = psBusqueda.trim();

    if (psSeleccionado && termino !== psSeleccionado.ps_nombre) {
      setPsSeleccionado(null);
      setContexto(null);
    }

    if (termino.length < 2) {
      setPsResultados([]);
      setPsBuscando(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setPsBuscando(true);
      try {
        const datos = await buscarPuntosServicioVisitaUE(termino);
        if (!activo) return;
        setPsResultados((datos || []).slice(0, 8));
      } catch {
        if (!activo) return;
        setPsResultados([]);
      } finally {
        if (activo) setPsBuscando(false);
      }
    }, 250);

    return () => {
      activo = false;
      window.clearTimeout(timeoutId);
    };
  }, [psBusqueda, psSeleccionado]);

  const seleccionarPunto = async (
    ps: Pick<PuntoServicioUE, "ps_nombre" | "ps_codigo"> & { id?: number }
  ) => {
    setPsResultados([]);
    setPsBusqueda(ps.ps_nombre);
    try {
      const data = await obtenerContextoVisitaUE(
        ps.id ? { psId: ps.id } : { psCodigo: ps.ps_codigo }
      );
      const puntoResuelto = {
        id: Number(data?.ps?.id || ps.id || 0),
        ps_nombre: String(data?.ps?.nombre || ps.ps_nombre || ""),
        ps_codigo: String(data?.ps?.codigo || ps.ps_codigo || ""),
      };
      setPsSeleccionado(puntoResuelto);
      setPsBusqueda(puntoResuelto.ps_nombre);
      setContexto(data);

      // Autocompletar responsable si la variante lo soporta
      const autocompletar = opciones?.campos_cabecera?.responsable_visita?.autocompletar;
      if (autocompletar) {
        setResponsableVisita(String(data?.fiscal?.nombre || ""));
      }
    } catch (error: any) {
      setPsSeleccionado(null);
      setContexto(null);
      setToast({
        show: true,
        msg: error?.message || "No se pudo obtener el contexto del punto.",
        color: "danger",
      });
    }
  };

  const actualizarRespuesta = (codigo: string, valor: string) => {
    setRespuestas((prev) => ({ ...prev, [codigo]: valor }));
  };

  const limpiarFormulario = () => {
    setPsBusqueda("");
    setPsResultados([]);
    setPsSeleccionado(null);
    setContexto(null);
    setPsNombreLibre("");
    setFechaVisita(undefined);
    setSector("");
    setResponsableVisita(opciones?.campos_cabecera?.responsable_visita?.valor_defecto || "");
    setMotivoVisita("");
    setRespuestas({});
  };

  const alternarNoEsCliente = (valor: boolean) => {
    setNoEsCliente(valor);
    // Al cambiar de modo, se limpia la seleccion/tipeo del otro.
    setPsBusqueda("");
    setPsResultados([]);
    setPsSeleccionado(null);
    setContexto(null);
    setPsNombreLibre("");
  };

  const guardar = async () => {
    if (noEsCliente) {
      if (!psNombreLibre.trim()) {
        setToast({ show: true, msg: "Debe ingresar el nombre del punto de servicio.", color: "warning" });
        return;
      }
    } else if (!psSeleccionado?.id) {
      setToast({ show: true, msg: "Debe seleccionar un punto de servicio.", color: "warning" });
      return;
    }
    if (!sector.trim()) {
      setToast({ show: true, msg: "Debe completar el sector.", color: "warning" });
      return;
    }
    const responsableCabecera = opciones?.campos_cabecera?.responsable_visita;
    const responsableRequerido = responsableCabecera?.requerido !== false;
    if (responsableRequerido && !responsableVisita.trim()) {
      const etiqueta = responsableCabecera?.texto;
      setToast({
        show: true,
        msg: etiqueta ? `Debe completar ${etiqueta}.` : "Debe completar el campo requerido.",
        color: "warning",
      });
      return;
    }
    if (!motivoVisita) {
      setToast({ show: true, msg: "Debe seleccionar el motivo de la visita.", color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      const datosPS = noEsCliente
        ? { es_cliente: false, ps_nombre: psNombreLibre.trim() }
        : { es_cliente: true, ps_id: psSeleccionado!.id, ps_codigo: psSeleccionado!.ps_codigo };
      await crearVisitaUE({
        ...datosPS,
        sector: sector.trim(),
        responsable_visita: responsableVisita.trim(),
        motivo_visita: motivoVisita,
        fecha_visita: fechaVisita,
        respuestas: preguntasOrdenadas.map((pregunta) => ({
          codigo: pregunta.codigo,
          respuesta: respuestas[pregunta.codigo] ?? "",
        })),
      });
      setToast({ show: true, msg: "Visita registrada correctamente.", color: "success" });
      limpiarFormulario();
      await cargarHistorial();
    } catch (error: any) {
      setToast({
        show: true,
        msg: error?.message || "No se pudo guardar la visita.",
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  const renderPregunta = (pregunta: PreguntaUE) => {
    const valor = respuestas[pregunta.codigo] || "";

    if (pregunta.categoria === "OPCIONES") {
      return (
        <IonRadioGroup
          value={valor}
          onIonChange={(e) =>
            actualizarRespuesta(pregunta.codigo, String(e.detail.value || ""))
          }
        >
          {pregunta.opciones.map((opcion) => (
            <IonItem key={`${pregunta.codigo}-${opcion.codigo}`}>
              <IonLabel>{opcion.etiqueta}</IonLabel>
              <IonRadio slot="start" value={opcion.codigo} />
            </IonItem>
          ))}
        </IonRadioGroup>
      );
    }

    if (pregunta.categoria === "OPCIONES_MULTIPLE") {
      const seleccionados = valor ? valor.split(",").filter(Boolean) : [];
      const alternar = (codigo: string, marcado: boolean) => {
        const set = new Set(seleccionados);
        if (marcado) set.add(codigo);
        else set.delete(codigo);
        actualizarRespuesta(pregunta.codigo, Array.from(set).join(","));
      };
      return (
        <IonList>
          {pregunta.opciones.map((opcion) => (
            <IonItem key={`${pregunta.codigo}-${opcion.codigo}`}>
              <IonLabel>{opcion.etiqueta}</IonLabel>
              <IonCheckbox
                slot="start"
                checked={seleccionados.includes(opcion.codigo)}
                onIonChange={(e) => alternar(opcion.codigo, e.detail.checked)}
              />
            </IonItem>
          ))}
        </IonList>
      );
    }

    if (pregunta.categoria === "FECHA") {
      return (
        <IonItem>
          <IonInput
            type="date"
            value={valor || ""}
            onIonChange={(e) =>
              actualizarRespuesta(pregunta.codigo, normalizarFecha(e.detail.value) || "")
            }
          />
        </IonItem>
      );
    }

    if (pregunta.categoria === "COMENTARIO") {
      return (
        <IonItem>
          <IonTextarea
            autoGrow
            value={valor}
            onIonChange={(e) =>
              actualizarRespuesta(pregunta.codigo, String(e.detail.value || ""))
            }
          />
        </IonItem>
      );
    }

    // TEXTO
    return (
      <IonItem>
        <IonInput
          value={valor}
          onIonChange={(e) =>
            actualizarRespuesta(pregunta.codigo, String(e.detail.value || ""))
          }
        />
      </IonItem>
    );
  };

  if (cargando) {
    return (
      <IonPage>
        <Header titulo="Visitas Unidades Especializadas" />
        <IonContent className="ion-padding">
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!acceso?.tiene_acceso) {
    return (
      <IonPage>
        <Header titulo="Visitas Unidades Especializadas" />
        <IonContent className="ion-padding">
          <IonCard>
            <IonCardContent>
              <IonText color="medium">
                No tiene acceso a este modulo. Si considera que es un error, comuniquese con el
                administrador del sistema.
              </IonText>
            </IonCardContent>
          </IonCard>
        </IonContent>
      </IonPage>
    );
  }

  const cabecera = opciones?.campos_cabecera;
  const motivos = cabecera?.motivo_visita?.opciones || [];

  return (
    <IonPage>
      <Header titulo="Visitas Unidades Especializadas" />
      <IonContent id="visitas-unidades-especializadas" className="ion-padding">
        <IonCard>
          <IonCardHeader>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <IonCardTitle>Punto de servicio</IonCardTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <IonCheckbox
                  checked={noEsCliente}
                  onIonChange={(e) => alternarNoEsCliente(e.detail.checked)}
                />
                <IonLabel
                  style={{ whiteSpace: "nowrap", cursor: "pointer" }}
                  onClick={() => alternarNoEsCliente(!noEsCliente)}
                >
                  No es cliente
                </IonLabel>
              </div>
            </div>
          </IonCardHeader>
          <IonCardContent>
            {noEsCliente ? (
              <IonItem>
                <IonLabel position="stacked">Nombre del punto / lugar</IonLabel>
                <IonInput
                  value={psNombreLibre}
                  placeholder="Escriba el nombre del lugar"
                  onIonChange={(e) => setPsNombreLibre(String(e.detail.value || ""))}
                />
              </IonItem>
            ) : (
              <>
                <IonItem>
                  <IonLabel position="stacked">Buscar punto de servicio</IonLabel>
                  <IonInput
                    value={psBusqueda}
                    placeholder="Ingrese nombre del punto"
                    onIonChange={(e) => setPsBusqueda(String(e.detail.value || ""))}
                  />
                </IonItem>
                <div style={{ display: "flex", gap: 12, paddingTop: 12 }}>
                  {psBuscando ? <IonSpinner name="crescent" /> : null}
                  {psSeleccionado?.id ? (
                    <IonBadge color="success">{psSeleccionado.ps_codigo}</IonBadge>
                  ) : null}
                </div>

                {psResultados.length > 0 ? (
                  <IonList>
                    {psResultados.map((ps) => (
                      <IonItem button key={ps.id} onClick={() => seleccionarPunto(ps)}>
                        <IonLabel>
                          <h2>{ps.ps_nombre}</h2>
                          <p>{ps.ps_codigo}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                ) : null}
              </>
            )}
          </IonCardContent>
        </IonCard>

        {noEsCliente || psSeleccionado?.id ? (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Nueva Visita</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ display: "flex", gap: 12, paddingBottom: 12 }}>
                <IonBadge color={noEsCliente ? "medium" : "success"}>
                  {noEsCliente ? "No cliente" : psSeleccionado?.ps_codigo}
                </IonBadge>
              </div>

              <IonItem>
                <IonLabel position="stacked">Fecha de Visita</IonLabel>
                <IonInput
                  type="date"
                  value={fechaVisita || ""}
                  onIonChange={(e) => setFechaVisita(normalizarFecha(e.detail.value))}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">
                  {cabecera?.sector?.texto || "Sector"}
                </IonLabel>
                <IonInput
                  value={sector}
                  onIonChange={(e) => setSector(String(e.detail.value || ""))}
                />
              </IonItem>

              {cabecera?.responsable_visita?.mostrar !== false ? (
                <IonItem>
                  <IonLabel position="stacked">
                    {cabecera?.responsable_visita?.texto || ""}
                  </IonLabel>
                  <IonInput
                    value={responsableVisita}
                    onIonChange={(e) =>
                      setResponsableVisita(String(e.detail.value || ""))
                    }
                  />
                </IonItem>
              ) : null}

              <IonItem>
                <IonLabel position="stacked">
                  {cabecera?.motivo_visita?.texto || "Motivo de la Visita"}
                </IonLabel>
                <IonSelect
                  value={motivoVisita}
                  placeholder="Seleccione"
                  onIonChange={(e) => setMotivoVisita(String(e.detail.value || ""))}
                >
                  {motivos.map((m) => (
                    <IonSelectOption key={m.codigo} value={m.codigo}>
                      {m.etiqueta}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              {preguntasOrdenadas.map((pregunta) => (
                <div key={pregunta.id} style={{ paddingTop: 16 }}>
                  <IonText>
                    <h3 style={{ marginBottom: 8 }}>
                      {pregunta.texto}
                      {pregunta.requerido ? " *" : ""}
                    </h3>
                  </IonText>
                  {renderPregunta(pregunta)}
                </div>
              ))}

              <div style={{ display: "flex", gap: 12, paddingTop: 20 }}>
                <IonButton onClick={guardar} disabled={guardando}>
                  {guardando ? <IonSpinner name="crescent" /> : "Guardar"}
                </IonButton>
                <IonButton fill="outline" onClick={limpiarFormulario} disabled={guardando}>
                  Limpiar
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        ) : null}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Mis visitas</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {listando ? (
              <IonSpinner name="crescent" />
            ) : historial.length === 0 ? (
              <IonText color="medium">Aun no existen visitas registradas.</IonText>
            ) : (
              <IonList>
                {historial.map((item) => (
                  <IonItem key={item.id}>
                    <IonLabel>
                      <h2>{item.ps_nombre}</h2>
                      <p>{item.fecha_visita || item.fecha_creacion}</p>
                      <p>
                        {item.motivo_visita ? `Motivo: ${item.motivo_visita}` : ""}
                        {item.sector ? ` - Sector: ${item.sector}` : ""}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3500}
          onDidDismiss={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      </IonContent>
    </IonPage>
  );
};

export default VisitasUnidadesEspecializadas;
